import { useCallback, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";
import { SalesReturnStatusEnum } from "@/features/sales-invoices";
import { ReturnableTypeEnum } from "../../returnable-type";
import { resolveInitialStockInMode } from "../../sales-return-rules";

export const SelectSalesInvoiceProductVariantSection = () => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const [salesInvoice, setSalesInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedSalesInvoiceProductVariants,
    setSelectedSalesInvoiceProductVariants,
  ] = useState([]);

  const { append } = useFieldArray({
    control,
    name: "returnableProductVariants",
  });

  const [
    buyer,
    seller,
    returnableProductVariants,
    salesReturnExpenses,
    returnableId,
    stockInMode,
  ] = useWatch({
    control,
    name: [
      "buyer",
      "seller",
      "returnableProductVariants",
      "salesReturnExpenses",
      "returnableId",
      "stockInMode",
    ],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  // Disable if buyer not selected OR if items exist from different source.
  // Attached expenses pin the source just as returned goods do.
  const disableBrowseSalesInvoice =
    !buyer ||
    ((returnableProductVariants?.length > 0 ||
      salesReturnExpenses?.length > 0) &&
      returnableId !== null);

  const fetchSalesInvoices = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!buyer || !seller) return [];

      const returnableStatuses = [
        SalesReturnStatusEnum.NONE,
        SalesReturnStatusEnum.PARTIALLY_RETURNED,
      ];

      const {
        data: { data },
      } = await outletApi.get("sales-invoices", {
        params: {
          buyerableId: buyer?.id,
          referenceNumber: searchValue || null,
          returnStatuses: returnableStatuses,
          excludeReturned: true,
          hasOutstanding: true,
          page,
          size,
        },
      });
      return data;
    },
    [seller, buyer, outletApi]
  );

  const fetchSalesInvoiceProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !seller) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `sales-invoices/${referenceNumber}/sales-invoice-product-variants`,
          {
            params: {
              withSalesOrder: true,
            },
          }
        );

        // Adjust remaining quantity based on existing return items
        const adjustedProductVariants = data
          .map((salesInvoiceProductVariant) => {
            const totalReturnedQuantity = returnableProductVariants
              .filter(
                (returnProductVariant) =>
                  returnProductVariant.returnableProductVariantId ===
                  salesInvoiceProductVariant.id
              )
              .reduce((total, variant) => total + variant.quantity, 0);

            return {
              ...salesInvoiceProductVariant,
              adjustedRemainingQuantity:
                salesInvoiceProductVariant.quantity - totalReturnedQuantity,
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0);

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Gagal memuat rincian faktur"),
          "error"
        );
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [seller, returnableProductVariants, outletApi, showSnackbar]
  );

  const handleSalesInvoiceChange = (newValue) => {
    setSalesInvoice(newValue);
    if (newValue) {
      // Record the source as soon as the invoice is picked rather than when
      // goods are confirmed: the Biaya tab needs its reference number to list
      // the invoice's expense lines, and a return may carry expenses and no
      // goods at all.
      setValue("returnableId", newValue.id);
      setValue("returnableType", ReturnableTypeEnum.SALES_INVOICE);
      setValue("returnableReferenceNumber", newValue.referenceNumber);
      fetchSalesInvoiceProductVariants(newValue.referenceNumber);
      setDialogOpen(true);
    } else {
      // Only reachable while nothing is attached — the field locks otherwise.
      setValue("returnableId", null);
      setValue("returnableReferenceNumber", null);
      setProductVariants([]);
    }
    setSelectedSalesInvoiceProductVariants([]);
  };

  const columns = [
    {
      field: "name",
      headerName: "Nama Barang",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Kuantitas",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Satuan",
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
  ];

  const submitSelection = () => {
    // Check if there's already a returnable source set
    if (returnableId && returnableId !== salesInvoice.id) {
      showSnackbar(
        "Anda hanya dapat memilih produk dari satu sumber dokumen. Hapus produk yang ada terlebih dahulu.",
        "error"
      );
      return;
    }

    // Set the returnable source if not already set
    if (!returnableId) {
      setValue("returnableId", salesInvoice.id);
      setValue("returnableType", ReturnableTypeEnum.SALES_INVOICE);
    }

    selectedSalesInvoiceProductVariants.forEach(
      ({
        id: salesInvoiceProductVariantId,
        salesOrderProductVariant,
        name,
        sku,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
        price,
        discount,
      }) => {
        // Get warehouseId from the correct path based on salesOrderableType
        const warehouseId =
          salesOrderProductVariant.salesOrder?.warehouseId ||
          salesOrderProductVariant.salesOrderLensProduct?.salesOrder
            ?.warehouseId;
        const alreadyExists = returnableProductVariants.some(
          (variant) =>
            variant.returnableProductVariantId === salesInvoiceProductVariantId
        );

        if (!alreadyExists) {
          // Determine initial stockInMode based on SRT's stockInMode
          // For PARTIAL mode, default to FULL

          append({
            id: ulid(),
            returnableProductVariantId: salesInvoiceProductVariantId,
            warehouseId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            price: price ?? 0,
            discount: discount ?? 0,
            note: null,
            stockInMode: resolveInitialStockInMode(stockInMode),
          });
        }
      }
    );

    // Close dialog
    setDialogOpen(false);
    setProductVariants([]);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setProductVariants([]);
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={salesInvoice}
        onChange={handleSalesInvoiceChange}
        disabled={disableBrowseSalesInvoice}
        fetchOptions={fetchSalesInvoices}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowseSalesInvoice
            ? !buyer
              ? "Pilih Pelanggan terlebih dahulu"
              : "Tidak dapat mengubah sumber dokumen setelah menambah produk"
            : undefined,
          InputProps: {
            placeholder: "Cari No. Referensi Faktur Penjualan",
          },
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Rincian Barang
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DataGrid
            getRowHeight={() => "auto"}
            apiRef={apiRef}
            columns={columns}
            rows={productVariants}
            loading={loadingProductVariants}
            checkboxSelection
            onRowSelectionModelChange={() => {
              const selectedModels = Array.from(
                gridRowSelectionIdsSelector(apiRef.current.state).values()
              );
              setSelectedSalesInvoiceProductVariants(selectedModels);
            }}
            sx={{ minHeight: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            variant="contained"
            disabled={selectedSalesInvoiceProductVariants.length === 0}
            onClick={submitSelection}
          >
            Lanjut
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
